import type { TodoNoteItemDraft, TodoNoteItemView } from '../types/todoNote';
import { addDays, parseISODate, toISODate } from './date';
import { compareTodoItemsForSort } from './todoNoteBucket';
import { compareTodoFavoriteFirst } from './todoNoteFavorite';
import { expandTodoItemsForView } from './todoRoutine';

function addDaysIso(baseIso: string, days: number): string {
  return toISODate(addDays(parseISODate(baseIso), days));
}

function monthKeyFromIso(iso: string): string {
  return String(iso ?? '').slice(0, 7);
}

function buildTouchedMonthKeysByNext14Days(todayIso: string): Set<string> {
  const out = new Set<string>();
  for (let i = 1; i <= 14; i += 1) {
    out.add(monthKeyFromIso(addDaysIso(todayIso, i)));
  }
  return out;
}

function sortIncompleteTodosAll(a: TodoNoteItemDraft, b: TodoNoteItemDraft): number {
  const ad = String(a.due_date ?? '').trim();
  const bd = String(b.due_date ?? '').trim();
  if (!ad && !bd) return a.id.localeCompare(b.id);
  if (!ad) return 1;
  if (!bd) return -1;
  return compareTodoItemsForSort(a, b);
}

function inTwoWeekWindow(
  item: TodoNoteItemView,
  todayIso: string,
  futureLimitIso: string,
  touchedMonths: Set<string>
): boolean {
  const dueIso = String(item.due_date ?? '').trim();
  if (!dueIso) return false;
  if (item.due_kind === 'month') {
    return touchedMonths.has(monthKeyFromIso(dueIso));
  }
  if (dueIso <= todayIso) return true;
  return dueIso <= futureLimitIso;
}

/**
 * 데일리 TODO: 루틴은 부모 더미 마감일이 아니라
 * 반복 시작~마감 × 표시 기간 × 요일의 발생일로 펼친 뒤 미완료만 남긴다.
 */
export function listDashboardDailyIncompleteTodos(
  items: TodoNoteItemDraft[],
  todayIso: string,
  mode: 'twoWeek' | 'all'
): TodoNoteItemView[] {
  if (!items.length) return [];
  const displayed = expandTodoItemsForView(items, todayIso).filter((item) => !item.completed);
  if (mode === 'all') {
    return [...displayed].sort(
      (a, b) => compareTodoFavoriteFirst(a, b) || sortIncompleteTodosAll(a, b)
    );
  }
  const futureLimitIso = addDaysIso(todayIso, 14);
  const touchedMonths = buildTouchedMonthKeysByNext14Days(todayIso);
  return displayed
    .filter((item) => inTwoWeekWindow(item, todayIso, futureLimitIso, touchedMonths))
    .sort((a, b) => compareTodoFavoriteFirst(a, b) || compareTodoItemsForSort(a, b));
}
